import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';
import { IpService } from 'src/app/services/ip.service';
import { LocationService } from 'src/app/services/location.service';
import { ExchangeRateService } from 'src/app/services/page-service/exchange-rate/exchange-rate.service';
import { environment } from 'src/environments/environment';
import * as moment from 'moment-timezone';
import { User } from 'src/app/models';
import { filter } from 'rxjs/operators';

const timezone: string = moment.tz.guess();

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit {
  hideLayout = false;
  isNavbarCollapsed = true;
  exchangeRate: string = '';

  toggleNavbar() {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  closeNavbar() {
    this.isNavbarCollapsed = true;
  }

  currUser: Observable<any>;
  countryFlagLink: string = '';

  form: FormGroup;
  isSubmitted: boolean = false;
  isSucceeded: boolean = false;

  isMobileNavShown: boolean = false;
  isAuthBtnShown: boolean = true;

  telephone: string = environment.contact.telephone_us;
  telephone_canada: string = environment.contact.telephone_canada;
  telephone_uk: string = environment.contact.telephone_uk;
  ph_mobile: {
    provider: string;
    contact_number: string;
    additional_details?: string;
  }[] = environment.contact.ph_mobile;

  images: any = JSON.parse(localStorage.getItem('res_images'));

  private exchangeRateKey = 'EXCHANGE_RATE';

  constructor(
    private authService: AuthService,
    private dataService: DataService,
    private router: Router,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private ipService: IpService,
    private locationService: LocationService,
    private exchangeRateService: ExchangeRateService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects;
        this.hideLayout =
          url.startsWith('/login') ||
          url.startsWith('/register') ||
          url.startsWith('/forgot-password') ||
          url.startsWith('/reset-password');
      });
  }

  ngOnInit() {
    this.currUser = this.authService.user;

    this.currUser.subscribe((user) => {
      if (user) {
        this.isAuthBtnShown = false;
      } else {
        this.isAuthBtnShown = true;
      }
    });

    this.getCountryFlag();
    this.fetchExchangeRate();

    this.form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  async fetchExchangeRate() {
    try {
      const storedRate = localStorage.getItem(this.exchangeRateKey);

      if (storedRate) {
        const exchangeRateData = JSON.parse(storedRate);
        if (exchangeRateData && exchangeRateData.Rate) {
          this.exchangeRate = `₱${parseFloat(exchangeRateData.Rate).toFixed(
            2
          )}`;
        }
      }

      const exchangeRateData = await this.exchangeRateService
        .loadExchangeRate()
        .toPromise();

      if (exchangeRateData && exchangeRateData.Rate) {
        this.exchangeRate = `₱${parseFloat(exchangeRateData.Rate).toFixed(2)}`;

        localStorage.setItem(
          this.exchangeRateKey,
          JSON.stringify(exchangeRateData)
        );
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);

      const storedRate = localStorage.getItem(this.exchangeRateKey);
      if (storedRate) {
        const exchangeRateData = JSON.parse(storedRate);
        if (exchangeRateData && exchangeRateData.Rate) {
          this.exchangeRate = `₱${parseFloat(exchangeRateData.Rate).toFixed(
            2
          )}`;
        }
      } else {
        this.exchangeRate = 'N/A';
      }
    }
  }

  isActive(url: string): boolean {
    return this.router.url.startsWith(url);
  }

  async getCountryFlag() {
    this.locationService.getLocation().subscribe(
      async (pos: GeolocationPosition) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        const geocoded = await this.locationService.getAddress(
          latitude,
          longitude
        );
        const clientTimeZone = timezone;
        let countryCode = geocoded.countryObj.short_name.toLowerCase();
        if (!countryCode || countryCode === '' || countryCode === null) {
          countryCode = 'us';
        }

        localStorage.setItem(environment.CLIENT_COUNTRY, countryCode);
        localStorage.setItem(environment.CLIENT_TIMEZONE, clientTimeZone);
        this.countryFlagLink = `../../../assets/img/flags/${countryCode}.svg`;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  toggleMobileNav() {
    this.isMobileNavShown = !this.isMobileNavShown;
  }

  logout() {
    this.authService.logout();
    this.router.navigateByUrl('home', { replaceUrl: true });
  }

  openSocialLink(link: string) {
    window.open(link, '_blank');
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.toastr.warning('Please complete required fields.');
      return;
    }

    try {
      this.isSubmitted = true;
      this.spinner.show();
      await this.dataService
        .sendNewsLetterSubscription(this.form.value)
        .toPromise();
      this.spinner.hide();
      this.form.reset();
      window.scrollTo(0, 0);
      this.isSucceeded = true;
      this.toastr.success(
        'Please check your email regularly.',
        'Sent Successfully!'
      );
    } catch (error) {
      this.toastr.error(
        'Someting went wrong sending request. Please try again.',
        'Oops!'
      );
      console.log(error.error);
      this.spinner.hide();
    }
  }

  get formControl() {
    return this.form.controls;
  }

  get version() {
    return environment.version;
  }

  get groupedPhMobile() {
    return this.ph_mobile.length > 0
      ? Object.entries(
          this.ph_mobile.reduce((prev, curr) => {
            const key = curr.provider;

            if (!prev[key]) {
              prev[key] = [];
            }

            prev[key].push(curr);

            return prev;
          }, [])
        )
      : [];
  }

  downloadApp(device: string): void {
    if (device === 'android') {
      if (navigator.userAgent.toLowerCase().indexOf('android') > -1) {
        window.location.href = 'market://details?id=com.axp.customer';
      } else {
        window.location.href = environment.appStoreLink[device];
      }
    } else if (device === 'ios') {
      if (navigator.userAgent.toLowerCase().indexOf('ios') > -1) {
        window.location.href = 'market://details?id=com.axp.customer';
      } else {
        window.location.href = environment.appStoreLink[device];
      }
    }
  }
}
